import React from 'react';

import {Button, Icon, Link, Modal, Text} from '#component/base';
import {DrawerProps} from '#component/modal/ActionPlanItemDrawer/ActionPlanItemDrawer.types';
import {ImpactImage} from '#component/page/ActionPlan/components/ImpactImage';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';
import {getCategoryIconName} from '#util';

import * as css from './ActionPlanItemDrawer.css';

function ActionPlanItemDrawer({
  children,
  imageUrl,
  impactLevel,
  title,
  subtitle,
  categoryKey,
  selected,
  onAdd,
  onRemove,
}: DrawerProps) {
  const {isDesktop} = useViewportSize();
  return (
    <Modal layout={isDesktop ? 'primary' : 'drawer'} header={<div />} className={css.dialogContent}>
      <div
        className={sprinkles({
          w: 'full',
        })}>
        <div
          className={sprinkles({
            display: 'flex',
            dir: 'col',
            w: 'full',
            flexGrow: 1,
            pB: 24,
          })}>
          <ImpactImage imageUrl={imageUrl} impactLevel={impactLevel} increaseSize />
        </div>
        <Text
          as="p"
          color="deepBurgundy"
          variant="body"
          fontWeight="semiBold"
          className={sprinkles({
            display: 'flex',
            gap: 12,
            items: 'center',
          })}>
          <Icon
            name={getCategoryIconName(categoryKey)}
            color="deepBurgundy"
            className={sprinkles({
              mR: 4,
            })}
          />
          {title}
        </Text>
        <Text
          as="h2"
          color="deepBurgundy"
          variant="bodyLarge"
          className={sprinkles({
            mT: 8,
            mB: 24,
          })}
          style={{
            textAlign: 'left',
            fontSize: 32,
            lineHeight: '40px',
          }}>
          {subtitle}
        </Text>
        {!isDesktop && onAdd && onRemove && (
          <ModalActionBar onRemove={onRemove} onAdd={onAdd} selected={selected} />
        )}
      </div>
      <div
        className={sprinkles({
          pB: 32,
          w: 'full',
        })}>
        {children}
      </div>
    </Modal>
  );
}

const ModalActionBar = ({
  selected,
  onAdd,
  onRemove,
}: Pick<DrawerProps, 'selected' | 'onAdd' | 'onRemove'>) => (
  <div
    className={sprinkles({
      display: 'flex',
      content: 'between',
      w: 'full',
      mB: 24,
    })}>
    {selected ? (
      <div
        className={sprinkles({
          display: 'flex',
          content: 'between',
          items: 'center',
          w: 'full',
        })}>
        <TagAlert />
        <Link
          onClick={onRemove}
          className={sprinkles({
            display: 'flex',
            items: 'center',
            w: 'maxContent',
          })}>
          <Icon
            name="Trash"
            className={sprinkles({
              mR: 8,
            })}
          />
          <Text variant="body">Remove Item</Text>
        </Link>
      </div>
    ) : (
      <Button
        variant="primary"
        className={sprinkles({
          w: 'full',
          mB: 24,
        })}
        onClick={onAdd}>
        Add to Action Plan
      </Button>
    )}
  </div>
);

const TagAlert = () => (
  <div
    className={sprinkles({
      display: 'flex',
      bg: 'solidGreen',
      borderRadius: 28,
      w: 'maxContent',
      items: 'center',
      pL: 8,
      pR: 20,
      p: 8,
    })}>
    <div
      className={sprinkles({
        w: 32,
        h: 32,
        display: 'flex',
        items: 'center',
        content: 'center',
        borderRadius: 'circle',
        bg: 'cotton',
        mR: 8,
      })}>
      <Icon name="Check" color="solidGreen" />
    </div>
    <Text color="cotton" fontWeight="semiBold">
      Goal Added!
    </Text>
  </div>
);

export {ActionPlanItemDrawer};
